import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import {
  Form,
  FormColumn,
  FormColumns,
  FormGroupContainer,
  FormRow,
  TextAreaContainer,
  TextInputContainer,
} from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from 'components/ModalSheet'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import { match } from 'utils/pattern-match-util'
import { pluralize } from 'utils/pluralization-util'
import { ClientInvitationViewModel } from './ClientInvitationViewModel'

interface CustomerInvitationFormProps {
  vm: ClientInvitationViewModel
}

const CustomerInvitationForm = observer(
  ({ vm }: CustomerInvitationFormProps) => {
    return (
      <>
        <FormRow>
          <FormGroupContainer
            label="Email address"
            error={vm.customerValidation.getError('customerEmail')}
          >
            <TextInputContainer
              model={vm}
              field="customerEmail"
              type="email"
              placeholder="customer@example.com"
              disabled={vm.submit.pending}
              data-test="client-invitation-customer-email"
            />
          </FormGroupContainer>
        </FormRow>

        <FormRow>
          <FormColumns>
            <FormColumn>
              <FormGroupContainer
                label="First name"
                error={vm.customerValidation.getError('customerGivenName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerGivenName"
                  placeholder="John"
                  disabled={vm.submit.pending}
                  data-test="client-invitation-customer-given-name"
                />
              </FormGroupContainer>
            </FormColumn>
            <FormColumn>
              <FormGroupContainer
                label="Last name"
                error={vm.customerValidation.getError('customerFamilyName')}
              >
                <TextInputContainer
                  model={vm}
                  field="customerFamilyName"
                  placeholder="Doe"
                  disabled={vm.submit.pending}
                  data-test="client-invitation-customer-family-name"
                />
              </FormGroupContainer>
            </FormColumn>
          </FormColumns>
        </FormRow>

        <FormRow>
          <FormGroupContainer
            label="Phone number"
            sublabel="(optional)"
            error={vm.customerValidation.getError('customerPhoneNumber')}
          >
            <TextInputContainer
              model={vm}
              field="customerPhoneNumber"
              type="tel"
              placeholder="+1 555-123-4567"
              disabled={vm.submit.pending}
              data-test="client-invitation-customer-phone"
            />
          </FormGroupContainer>
        </FormRow>
      </>
    )
  },
)

interface RegularInvitationFormProps {
  vm: ClientInvitationViewModel
}

const RegularInvitationForm = observer(({ vm }: RegularInvitationFormProps) => {
  return (
    <>
      <FormRow>
        <FormGroupContainer
          label="Email address"
          error={vm.invitationValidation.getError('emailAddressTextArea')}
        >
          <TextAreaContainer
            model={vm}
            field="emailAddressTextArea"
            rows={3}
            placeholder="e.g. timmy.taxes@taxfyle.com, cindy.cpa@firm.com"
            help="Enter one or more email addresses, separated by commas."
            data-test="client-invitation-email-textarea"
          />
        </FormGroupContainer>
      </FormRow>
    </>
  )
})

interface ClientInvitationDialogProps {
  vm: ClientInvitationViewModel
}

export const ClientInvitationDialog = observer(
  ({ vm }: ClientInvitationDialogProps) => {
    const renderForm = () =>
      match(vm.signInType, {
        AccessLink: () => <CustomerInvitationForm vm={vm} />,
        UsernamePassword: () => <RegularInvitationForm vm={vm} />,
      })

    return (
      <ModalSheet
        showing={vm.showing}
        onRequestClose={vm.close}
        disableCloseOnOutsideClick
      >
        <ModalSheetHeader withCloseButton />

        <ModalSheetContent>
          <ModalSheetSection horizontallySpaced>
            <ModalSheetContentTitle>Invite client</ModalSheetContentTitle>
            <ModalSheetContentDescription compact>
              {vm.signInType === 'AccessLink'
                ? 'Fill in the customer information to allow access link-based sign in.'
                : "Fill in the emails of the users you'd like to invite to the Customer Portal."}
            </ModalSheetContentDescription>
          </ModalSheetSection>

          <ModalSheetSection spaced>
            <Form id="clientInvitation" onSubmit={vm.submit}>
              {vm.canUseAccessLink && vm.canUseUsernameAndPassword && (
                <FormRow>
                  <RadioButtonGroup label="Sign-in Type" size="big-wide">
                    <RadioButtonItem
                      active={vm.signInType === 'UsernamePassword'}
                      onClick={() => vm.setSignInType('UsernamePassword')}
                      disabled={vm.submit.pending}
                    >
                      Username & Password
                    </RadioButtonItem>
                    <RadioButtonItem
                      active={vm.signInType === 'AccessLink'}
                      onClick={() => vm.setSignInType('AccessLink')}
                      disabled={vm.submit.pending}
                    >
                      Access Link
                    </RadioButtonItem>
                  </RadioButtonGroup>
                </FormRow>
              )}

              {renderForm()}
            </Form>
          </ModalSheetSection>
        </ModalSheetContent>

        <ModalSheetFooter>
          <ModalSheetFooterSpacer />
          <ModalSheetFooterButtonGroup>
            <Button type="default" onClick={vm.close}>
              Close
            </Button>
            <Button
              submit
              form="clientInvitation"
              type="primary"
              disabled={vm.submit.pending}
              dataTest="client-invitation-submit"
            >
              {vm.signInType === 'AccessLink'
                ? 'Create new customer'
                : `Send ${pluralize('invite', vm.emailAddresses.length)}`}
            </Button>
          </ModalSheetFooterButtonGroup>
        </ModalSheetFooter>
      </ModalSheet>
    )
  },
)
